import { useState, useEffect, useContext, useRef } from "react";
import Loader from "../../utils/Loading";
import styles from "./styles.module.css";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import FormTextField from "../../utils/CustomFields/FormTextField";
import { ROUTES, KEYBOARD_KEYS, SYSTEM_VIEW, STORAGE_KEYS } from "const";
import ShopContext from "context/ShopContext";
import Button from "@mui/material/Button";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import * as TerminalHelper from "helpers/TermialHelper";
import * as ShiftHelper from "helpers/ShiftHelper";
import ShopsHelper from "helpers/ShopsHelper";
import toast from "react-hot-toast";

function Shift() {
  const navigate = useNavigate();
  const paymentRefs = useRef([]);
  const shiftNameRef = useRef(null);

  useEffect(() => {
    const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
    if (shiftId) {
      const settings = TerminalHelper.getTerminalSettings();
      const { isTableDashboardEnabled, isOrderDashboardEnabled, systemType } = settings;
      const shopProfile = ShopsHelper.getCachedShopProfile();
      const hasFullKotValidity = ShopsHelper.hasFullKotValidity(shopProfile);

      if (systemType === SYSTEM_VIEW.ERP) {
        navigate(ROUTES.ERP_DASHBOARD, { replace: true });
      } else if (isTableDashboardEnabled && hasFullKotValidity) {
        navigate(ROUTES.TABLE_DASHBOARD, { replace: true });
      } else if (isOrderDashboardEnabled) {
        navigate(ROUTES.ACTIVE_ORDERS, { replace: true });
      } else {
        navigate(ROUTES.SALES, { replace: true });
      }
      return;
    }

    setTimeout(() => {
      shiftNameRef.current?.focus();
    }, 100);
  }, [navigate]);

  const { paymentModes } = useContext(ShopContext);

  const [isLoading, setIsLoading] = useState(false);
  const [shiftName, setShiftName] = useState(ShiftHelper.getShiftName());
  const [tenders, setTenders] = useState([]);

  const onClickCreateShiftHandler = async () => {
    if (!shiftName) {
      toast.error("Shift name should not be empty.");
      return;
    }
    setIsLoading(true);
    try {
      const startTime = DateTimeHelpers.getLocalISOString();
      const payload = ShiftHelper.buildCreateShiftPayload(shiftName, tenders, startTime);

      const settings = TerminalHelper.getTerminalSettings();
      const { isTableDashboardEnabled, isOrderDashboardEnabled, systemType } = settings;
      const shopProfile = ShopsHelper.getCachedShopProfile();
      const hasFullKotValidity = ShopsHelper.hasFullKotValidity(shopProfile);

      await ShiftHelper.performShiftCreation(payload, tenders, startTime);

      if (systemType === SYSTEM_VIEW.ERP) {
        navigate(ROUTES.ERP_DASHBOARD);
      } else if (isTableDashboardEnabled && hasFullKotValidity) {
        navigate(ROUTES.TABLE_DASHBOARD);
      } else if (isOrderDashboardEnabled) {
        navigate(ROUTES.ACTIVE_ORDERS);
      } else {
        navigate(ROUTES.SALES);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to process shift creation");
    }
    setIsLoading(false);
  };

  const handleTenderChange = (paymentModeId, value) => {
    if (!/^\d*\.?\d*$/.test(value)) return;

    const clean = value.replace(/^0+(?=\d)/, "");

    const updated = tenders.map((t) => (t.payment_mode.id === paymentModeId ? { ...t, amount: clean } : t));

    setTenders(updated);
  };

  useEffect(() => {
    if (paymentModes && paymentModes.length > 0) {
      const initialTenders = paymentModes.map((mode) => ({
        payment_mode: mode,
        amount: "0",
        cash_in: 0,
        cash_out: 0,
        closing: 0,
      }));
      setTenders(initialTenders);
    }
  }, [paymentModes]);

  const handleShiftSetup = (e) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      paymentRefs.current[0]?.focus();
    }
  };

  const handleTenderKeyDown = (e, index) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      e.preventDefault();

      const totalModes = paymentModes?.length || 0;

      if (index < totalModes - 1) {
        paymentRefs.current[index + 1]?.focus();
      } else {
        onClickCreateShiftHandler();
      }
    }
  };

  return (
    <>
      <Loader
        isOpen={isLoading}
        backdropSx={{
          borderRadius: "16px",
        }}
      />
      <Grid container rowSpacing={4} className={styles.textArea}>
        <Grid item xs={12}>
          <Grid container rowSpacing={1}>
            <Grid item xs={12} display="flex" justifyContent={"center"}>
              <span className={styles.head1}>
                Shift <span className={styles.head2}>Setup</span>
              </span>
            </Grid>
            <Grid item xs={12} className={styles.text}>
              <span>Please set the shift setup for continue billing.</span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingTop: "24px" }}>
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <FormTextField
                id="shift-name"
                inputRef={shiftNameRef}
                label="Shift name"
                value={shiftName || ""}
                onChange={(e) => setShiftName(e.target.value)}
                onKeyDown={handleShiftSetup}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} display="flex" justifyContent={"center"} style={{ paddingTop: "24px" }}>
          <span className={styles.title}>Opening Balance</span>
        </Grid>
        <Grid item xs={12} style={{ paddingTop: "12px" }}>
          <Grid container rowSpacing={2} columnSpacing={2}>
            {(paymentModes || []).map((paymentMode, index) => (
              <Grid item xs={12} sm={6} key={paymentMode.id}>
                <FormTextField
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*"
                  id={`tender-${paymentMode.id}`}
                  label={paymentMode.name}
                  value={tenders.find((tender) => tender.payment_mode.id === paymentMode.id)?.amount || ""}
                  onChange={(e) => handleTenderChange(paymentMode.id, e.target.value)}
                  onFocus={(e) => e.target.select()}
                  inputRef={(el) => (paymentRefs.current[index] = el)}
                  onKeyDown={(e) => handleTenderKeyDown(e, index)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "46px",
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingTop: "24px" }}>
          <Button variant="contained" onClick={onClickCreateShiftHandler} fullWidth className={styles.btn}>
            Continue
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export default Shift;
